import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { ContractResponse } from '@timelog/pm-shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * BR-7: the API refused the delete with a 409 because plan tasks bill through
 * the contract. The user picks a sibling and the delete is reissued with
 * `?reassignTo` — references are rewritten transactionally server-side.
 */
export type DeleteContractDialogProps = {
  /** The contract the 409 was for; null closes the dialog. */
  contract: ContractResponse | null;
  /** The API's 409 message (carries the referencing-task count). */
  message: string;
  siblings: ContractResponse[];
  onConfirm: (reassignTo: string) => Promise<void>;
  onClose: () => void;
};

export const DeleteContractDialog = ({
  contract,
  message,
  siblings,
  onConfirm,
  onClose,
}: DeleteContractDialogProps) => {
  const [reassignTo, setReassignTo] = useState('');
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);
  const target = reassignTo || (siblings[0]?.id ?? '');

  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await onConfirm(target);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={contract !== null}
      onClose={busy ? undefined : onClose}
      data-automation-id="delete-contract-dialog"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('deleteDialog.Title', { name: contract?.name ?? '' })}</DialogTitle>
      <DialogContent>
        <Typography variant="bodyRegular" color="text.secondary" sx={{ mb: 2 }}>
          {message}
        </Typography>
        <Typography variant="bodyRegular" sx={{ mb: 1 }}>
          {t('deleteDialog.MoveTasksTo')}
        </Typography>
        <Select
          value={target}
          onChange={(event) => setReassignTo(event.target.value)}
          size="small"
          fullWidth
          data-automation-id="delete-reassign-select"
        >
          {siblings.map((sibling) => (
            <MenuItem
              key={sibling.id}
              value={sibling.id}
              data-automation-id="delete-reassign-option"
            >
              {t('deleteDialog.Option', {
                name: sibling.name,
                method: t(`pmCommon:billingMethod.${sibling.type}`),
              })}
            </MenuItem>
          ))}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} color="inherit">
          {t('actions.Cancel')}
        </Button>
        <Button
          onClick={() => void confirm()}
          disabled={busy || !target}
          variant="contained"
          color="error"
          data-automation-id="delete-reassign-confirm"
        >
          {t('deleteDialog.Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
